import { Shield, Award, Trophy } from 'lucide-react';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function CompanyInsuranceStep({ data, onChange }: Props) {
  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const updateNestedField = (parent: string, field: string, value: any) => {
    onChange({
      ...data,
      [parent]: {
        ...(data[parent] || {}),
        [field]: value
      }
    });
  };

  const updateArrayField = (field: string, value: string[]) => {
    onChange({ ...data, [field]: value });
  };

  const addToArray = (field: string, value: string) => {
    if (!value.trim()) return;
    const currentArray = data[field] || [];
    updateArrayField(field, [...currentArray, value.trim()]);
  };

  const removeFromArray = (field: string, index: number) => {
    const currentArray = data[field] || [];
    updateArrayField(field, currentArray.filter((_: any, i: number) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Insurance & Compliance</h2>
        <p className="text-gray-600">Provide insurance and certification details</p>
      </div>

      {/* Insurance Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary-600" />
          Insurance Information
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Insurance Provider
            </label>
            <input
              type="text"
              value={data.insuranceProvider || ''}
              onChange={(e) => updateField('insuranceProvider', e.target.value)}
              placeholder="e.g., AXA, Allianz"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coverage Amount
            </label>
            <input
              type="text"
              value={data.insuranceCoverage || ''}
              onChange={(e) => updateField('insuranceCoverage', e.target.value)}
              placeholder="e.g., €2,000,000"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-2">Insurance Types</p>
          
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={data.insurance?.publicLiability || false}
              onChange={(e) => updateNestedField('insurance', 'publicLiability', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Public Liability Insurance</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={data.insurance?.professionalIndemnity || false}
              onChange={(e) => updateNestedField('insurance', 'professionalIndemnity', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Professional Indemnity Insurance</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={data.insurance?.employersLiability || false}
              onChange={(e) => updateNestedField('insurance', 'employersLiability', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Employers Liability Insurance</span>
          </label>
        </div>
      </div>

      {/* Professional Memberships */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary-600" />
          Professional Memberships
        </h3>

        <div>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              id="membership-input"
              placeholder="e.g., CIRI, NFRC"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const input = e.currentTarget;
                  addToArray('professionalMemberships', input.value);
                  input.value = '';
                }
              }}
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('membership-input') as HTMLInputElement;
                if (input) {
                  addToArray('professionalMemberships', input.value);
                  input.value = '';
                }
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-3">
            {(data.professionalMemberships || []).map((membership: string, index: number) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                {membership}
                <button
                  type="button"
                  onClick={() => removeFromArray('professionalMemberships', index)}
                  className="text-gray-500 hover:text-red-600 ml-1"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Awards */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary-600" />
          Awards & Recognition
        </h3>

        <div>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              id="award-input"
              placeholder="e.g., Best Service Provider 2024"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const input = e.currentTarget;
                  addToArray('awards', input.value);
                  input.value = '';
                }
              }}
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('award-input') as HTMLInputElement;
                if (input) {
                  addToArray('awards', input.value);
                  input.value = '';
                }
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-3">
            {(data.awards || []).map((award: string, index: number) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm"
              >
                {award}
                <button
                  type="button"
                  onClick={() => removeFromArray('awards', index)}
                  className="text-emerald-600 hover:text-red-600 ml-1"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
